import React, { useEffect, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import {
  AssetType,
  useGetAssetQuery,
  useUpsertAssetTypeMutation,
  useUpsertAssetLocationsMutation,
  useUpsertAssetMutation,
} from "../store/slices/mapsSlice";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import AssetLocations from "./AssetLocations";
import { OverlayTrigger, Popover, Tab, Tabs } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import AssetContacts from "./AssetContacts";
import AssetRelations from "./AssetRelations";
import { toast } from "react-toastify";
import AssetLocationsList from "./AssetLocationsList";
import { makeid } from "../helpers";
import { getOverlayLocation } from "../Maps/helpers";
import StyledTabContainer from "../Common/StyledTabContainer";
import {
  assetIconOptions,
  formatAssetKindOptionLabel,
  getAssetKindValueLabels,
  formatAssetTypeValue,
  loadAssetKindOptions,
} from "./assetIconsList";
import AssetsIcon from "./AssetsIcon";
import JsonCollapse from "../Builder/Components/JsonCollapse";
import { PlaceAutocomplete } from "../Contacts/PlacesSearch";
import { APIProvider } from "@vis.gl/react-google-maps";
import InputLabel from "../Builder/nodes/Logic/Inputs/InputLabel";
import HelpButton from "../HelpContent/HelpButton";
import Logs from "../Dashboard/Logs";
import { useConfirmDialog } from "../Builder/Components/ConfirmDialogContext";
import {
  clearAssetTypeOptionCache,
  getAssetTypeOptionCacheVersion,
  subscribeAssetTypeOptionCache,
} from "./assetTypeOptions";
import DeleteAssetsModal from "./DeleteAssetsModal";

const ASSET_ICON_PAGE_SIZE = 24;
const DEFAULT_ASSET_TYPE_ICON = "/default.svg";

type AssetSaveError = {
  message: string;
  reference?: string;
  duplicateAsset?: {
    uuid: string;
    label?: string;
    reference?: string;
  };
};

const stripValidationPrefix = (message: string) =>
  message.replace(/^validation error:\s*/i, "").trim();

const firstString = (value: any): string => {
  if (!value) {
    return "";
  }
  if (typeof value === "string") {
    return value;
  }
  if (Array.isArray(value)) {
    return firstString(value[0]);
  }
  return "";
};

const errorMessageFromPayload = (payload: any): string => {
  if (!payload) {
    return "";
  }
  if (typeof payload === "string") {
    return payload;
  }
  return (
    errorMessageFromPayload(payload.data) ||
    firstString(payload.error) ||
    firstString(payload.message) ||
    firstString(payload.errors?.Reference) ||
    firstString(payload.errors?.reference)
  );
};

const normalizeAssetSaveError = (payload: any): AssetSaveError => {
  const duplicateAssetPayload =
    payload?.data?.duplicate_asset || payload?.duplicate_asset;
  const duplicateAsset = duplicateAssetPayload?.uuid
    ? {
        uuid: duplicateAssetPayload.uuid,
        label: duplicateAssetPayload.label,
        reference: duplicateAssetPayload.reference,
      }
    : undefined;
  const message = stripValidationPrefix(
    errorMessageFromPayload(payload) || "Unable to save asset."
  );
  const reference = stripValidationPrefix(
    firstString(payload?.data?.errors?.Reference) ||
      firstString(payload?.data?.errors?.reference) ||
      firstString(payload?.errors?.Reference) ||
      firstString(payload?.errors?.reference) ||
      (message.toLowerCase().includes("reference") ? message : "")
  );

  return { message, reference: reference || undefined, duplicateAsset };
};

export function EditAssetForm({
  assetUuid,
  onClose,
  tab = "details",
}: {
  assetUuid: string;
  onClose?: () => void;
  tab?: string;
}) {
  const confirmDialog = useConfirmDialog() as
    | ((title: string, message: string) => Promise<boolean>)
    | null;
  const [selectedAsset, setSelectedAsset] = useState<Partial<AssetType>>({});
  const [newAssetTypeLabel, setNewAssetTypeLabel] = useState("");
  const [newAssetTypeIcon, setNewAssetTypeIcon] = useState(
    DEFAULT_ASSET_TYPE_ICON
  );
  const [assetIconSearch, setAssetIconSearch] = useState("");
  const [assetIconLimit, setAssetIconLimit] = useState(ASSET_ICON_PAGE_SIZE);
  const [saveError, setSaveError] = useState<AssetSaveError | null>(null);
  const [showAssetTypeIconPopover, setShowAssetTypeIconPopover] =
    useState(false);
  const [showDeleteAssetModal, setShowDeleteAssetModal] = useState(false);
  const [assetTypeCacheVersion, setAssetTypeCacheVersion] = useState(
    getAssetTypeOptionCacheVersion()
  );
  const assetTypePopoverContainerRef = useRef<HTMLDivElement | null>(null);
  const assetIconSearchRef = useRef<HTMLInputElement | null>(null);
  const { data: asset } = useGetAssetQuery(assetUuid || "", {
    skip: assetUuid === "new" || !assetUuid,
  });

  useEffect(() => {
    const unsubscribe = subscribeAssetTypeOptionCache(setAssetTypeCacheVersion);
    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (assetUuid === "new") {
      setSaveError(null);
      setSelectedAsset({
        // Uuid: shortUUID(),
        Label: "New asset",
        Kind: "asset",
        HasLocation: false,
        CanMove: false,
        Reference: makeid(8),
      });
      return;
    }
    if (asset) {
      setSaveError(null);
      setSelectedAsset(asset);
    }
  }, [asset, assetUuid]);

  useEffect(() => {
    if (showAssetTypeIconPopover) {
      window.setTimeout(() => assetIconSearchRef.current?.focus(), 0);
    }
  }, [showAssetTypeIconPopover]);

  const handleClose = () => {
    setSelectedTab("details");
    setSaveError(null);
    onClose?.();
  };
  const [saveAsset, result] = useUpsertAssetMutation();
  const [saveAssetType] = useUpsertAssetTypeMutation();
  const [saveAssetLocation, resultLocation] = useUpsertAssetLocationsMutation();
  const [locationOverlays, setLocationOverlays] = useState<any>([]);
  const navigate = useNavigate();

  const handleSave = async () => {
    setSaveError(null);
    const data: any = await saveAsset(selectedAsset);
    if (data.error) {
      setSaveError(normalizeAssetSaveError(data.error));
      return;
    }
    const r = data.data;

    if (r) {
      toast.success("Asset saved successfully");
      setSelectedAsset(r);

      if (selectedAsset.HasLocation && selectedAsset.Uuid) {
        const body = locationOverlays
          .filter((o: any) => !o.clusterData || !o.clusterData.IsCluster)
          .map((o: any) => ({
            ...getOverlayLocation(o),
          }));
        const res: any = await saveAssetLocation({
          assetUuid: selectedAsset.Uuid || "",
          body,
        });
        if (res.error) {
          setSaveError(normalizeAssetSaveError(res.error));
          return;
        }
        // remove overlays
        locationOverlays.forEach((o: any) => {
          o.setMap(null);
        });
        setLocationOverlays([]);

        toast.success("Asset location saved successfully");
      }
      // setSelectedTab("details");

      // navigate(`/assets/assets/${r.Uuid}`, { replace: true });
    }
  };

  const [selectedTab, setSelectedTab] = useState(tab);

  const filteredAssetIconOptions = assetIconOptions.filter((option) =>
    option.label.toLowerCase().includes(assetIconSearch.trim().toLowerCase())
  );
  const orderedAssetIconOptions = assetIconSearch.trim()
    ? filteredAssetIconOptions
    : [
        ...filteredAssetIconOptions.filter(
          (option) => option.value === DEFAULT_ASSET_TYPE_ICON
        ),
        ...filteredAssetIconOptions.filter(
          (option) => option.value !== DEFAULT_ASSET_TYPE_ICON
        ),
      ];
  const visibleAssetIconOptions = orderedAssetIconOptions.slice(
    0,
    assetIconLimit
  );

  const handleAssetIconScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const isAtBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight < 12;
    if (isAtBottom && assetIconLimit < orderedAssetIconOptions.length) {
      setAssetIconLimit((limit) => limit + ASSET_ICON_PAGE_SIZE);
    }
  };

  const handleCreateAssetType = async () => {
    if (!newAssetTypeLabel.trim()) {
      return;
    }
    if (!newAssetTypeIcon) {
      toast.error("Select an icon for the new asset type.");
      return;
    }

    const response: any = await saveAssetType({
      Label: newAssetTypeLabel.trim(),
      Value: formatAssetTypeValue(newAssetTypeLabel),
      Icon: newAssetTypeIcon,
    });
    if (response.error) {
      console.log(response.error);
      return;
    }

    const savedType = response.data?.type;
    clearAssetTypeOptionCache([savedType?.Value || formatAssetTypeValue(newAssetTypeLabel)]);
    setSelectedAsset({
      ...selectedAsset,
      Kind: savedType?.Value || formatAssetTypeValue(newAssetTypeLabel),
    });
    setNewAssetTypeLabel("");
    setNewAssetTypeIcon(DEFAULT_ASSET_TYPE_ICON);
    setAssetIconSearch("");
    setAssetIconLimit(ASSET_ICON_PAGE_SIZE);
    setShowAssetTypeIconPopover(false);
    toast.success("Asset type saved successfully");
  };

  return (
    <>
      <Modal.Header closeButton onHide={handleClose}>
        <Modal.Title className="d-flex justify-content-between flex-grow-1">
          <div>
            {(assetUuid === "new" ? "Add Asset - " : "Edit Asset - ") +
              selectedAsset.Label}
            <HelpButton helpKey="edit-asset" />
          </div>
          <div className="fs-6"> Ref:{selectedAsset.Uuid}</div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
          <StyledTabContainer id="edit-asset-tabs">
            {saveError && (
              <div className="alert alert-danger mb-3" role="alert">
                {saveError.message}
              </div>
            )}
            <Tabs
              mountOnEnter
              activeKey={selectedTab}
              onSelect={(k: any) => setSelectedTab(k)}
            >
              <Tab
                title={
                  <>
                    <span>Asset details</span>
                    <HelpButton helpKey="asset-details" />
                  </>
                }
                eventKey="details"
              >
                <div className="p-3 d-flex flex-column" style={{ height: 700 }}>
                  <div className="flex-grow-1">
                    <div className="row">
                      <div className="col-12 mb-3 text-secondary">
                        Add the details of the Asset you wish to add or edit.
                      </div>
                      <div className="col-lg-6 mb-3">
                        <Input
                        label="Asset Label"
                          value={[selectedAsset?.Label]}
                          onChange={([v]: any) =>
                            setSelectedAsset({ ...selectedAsset, Label: v })
                          }
                          options={undefined}
                        />
                      </div>
                      <div
                        className="col-lg-6 mb-3"
                        ref={assetTypePopoverContainerRef}
                      >
                        <OverlayTrigger
                          trigger={[]}
                          placement="bottom"
                          show={showAssetTypeIconPopover}
                          container={assetTypePopoverContainerRef}
                          overlay={
                            <Popover
                              id="new-asset-type-icon-popover"
                              style={{ maxWidth: 460, width: 420 }}
                            >
                              <Popover.Header as="h3">
                                New asset type: {newAssetTypeLabel}
                              </Popover.Header>
                              <Popover.Body className="p-3">
                                <div className="small text-secondary mb-3">
                                  Please select an icon for the new asset.
                                </div>
                                <input
                                  ref={assetIconSearchRef}
                                  className="form-control form-control-sm mb-3"
                                  value={assetIconSearch}
                                  onChange={(e) => {
                                    setAssetIconSearch(e.target.value);
                                    setAssetIconLimit(ASSET_ICON_PAGE_SIZE);
                                  }}
                                  placeholder="Search icons"
                                />
                                <div
                                  className="d-flex flex-column gap-2"
                                  onScroll={handleAssetIconScroll}
                                  style={{
                                    maxHeight: 260,
                                    overflowY: "auto",
                                  }}
                                >
                                  {visibleAssetIconOptions.map((option) => (
                                    <button
                                      key={option.value}
                                      type="button"
                                      className={`btn btn-sm d-flex align-items-center gap-2 text-start ${
                                        newAssetTypeIcon === option.value
                                          ? "btn-colored"
                                          : "btn-outline-secondary"
                                      }`}
                                      title={option.label}
                                      onClick={() =>
                                        setNewAssetTypeIcon(option.value)
                                      }
                                    >
                                      <AssetsIcon
                                        assetKind={option.icon}
                                        white={newAssetTypeIcon === option.value}
                                      />
                                      <span className="text-truncate">
                                        {option.label}
                                      </span>
                                    </button>
                                  ))}
                                  {visibleAssetIconOptions.length === 0 && (
                                    <div className="text-secondary small py-2">
                                      No icons found.
                                    </div>
                                  )}
                                </div>
                                <div className="d-flex justify-content-end gap-2 mt-3">
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                      setNewAssetTypeLabel("");
                                      setNewAssetTypeIcon(DEFAULT_ASSET_TYPE_ICON);
                                      setAssetIconSearch("");
                                      setAssetIconLimit(ASSET_ICON_PAGE_SIZE);
                                      setShowAssetTypeIconPopover(false);
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    variant="colored"
                                    size="sm"
                                    onClick={handleCreateAssetType}
                                  >
                                    Save type
                                  </Button>
                                </div>
                              </Popover.Body>
                            </Popover>
                          }
                        >
                          <div>
                            <Input
                              key={`asset-kind-${assetTypeCacheVersion}`}
                              label="Asset Type"
                              value={[selectedAsset?.Kind]}
                              onChange={([v]: any) =>
                                setSelectedAsset({ ...selectedAsset, Kind: v })
                              }
                              inputType="select"
                              isValidNewOption={(
                                v: string,
                                _selectedOptions?: any,
                                selectOptions?: any[]
                              ) => {
                                const value = formatAssetTypeValue(v);
                                if (!value) {
                                  return false;
                                }

                                return !(selectOptions || []).some(
                                  (option) =>
                                    option.value === value ||
                                    formatAssetTypeValue(option.label) === value
                                );
                              }}
                              onCreateOption={(v: string) => {
                                setNewAssetTypeLabel(v);
                                setNewAssetTypeIcon(DEFAULT_ASSET_TYPE_ICON);
                                setAssetIconSearch("");
                                setAssetIconLimit(ASSET_ICON_PAGE_SIZE);
                                setShowAssetTypeIconPopover(true);
                              }}
                              options={undefined}
                              placeholder="Select or type to add new"
                              loadOptions={loadAssetKindOptions as any}
                              formatOptionLabel={formatAssetKindOptionLabel}
                              getValueLabels={getAssetKindValueLabels as any}
                            />
                          </div>
                        </OverlayTrigger>
                      </div>
                      <div className="col-lg-6 mb-3">
                        <Input
                          label="Description"
                          inputType="textArea"
                          value={[selectedAsset?.Description]}
                          onChange={([v]: any) =>
                            setSelectedAsset({
                              ...selectedAsset,
                              Description: v,
                            })
                          }
                        />
                      </div>

                      <div className="col-lg-6 mb-3">
                        <Input
                          label="Reference"
                          value={[selectedAsset?.Reference]}
                          onChange={([v]: any) => {
                            if (saveError?.reference) {
                              setSaveError(null);
                            }
                            setSelectedAsset({ ...selectedAsset, Reference: v });
                          }}
                          options={undefined}
                        />
                        {saveError?.reference && (
                          <div className="text-danger small mt-1">
                            {saveError.reference}
                            {saveError.duplicateAsset && (
                              <>
                                {" "}
                                <Link
                                  to={`/assets/assets/${saveError.duplicateAsset.uuid}`}
                                  onClick={handleClose}
                                >
                                  Open {saveError.duplicateAsset.label || "asset"}
                                  {saveError.duplicateAsset.reference
                                    ? ` (${saveError.duplicateAsset.reference})`
                                    : ""}
                                </Link>
                              </>
                            )}
                          </div>
                        )}
                        <span className="text-secondary small">
                          You can change the reference, if you wish to use a
                          custom one. It must be unique to this asset.
                        </span>
                      </div>

                      <div className="col-lg-6 mb-3">
                        <InputLabel>Search address</InputLabel>
                        <APIProvider
                          apiKey={(window as any).GM_API_KEY}
                          version="3.64"
                        >
                          <PlaceAutocomplete
                            onPlaceSelect={async (place) => {
                              setSelectedAsset({
                                ...selectedAsset,
                                Address: place?.formatted_address || "",
                              });
                              if (
                                confirmDialog &&
                                (await confirmDialog(
                                  "Add location?",
                                  "Would you like to add a location for this asset?"
                                ))
                              ) {
                                if (
                                  !place?.geometry?.location?.lng() ||
                                  !place?.geometry?.location?.lat()
                                ) {
                                  toast.error(
                                    "Selected place does not have a valid location."
                                  );
                                  return;
                                }

                                const assetResponse: any = await saveAsset({
                                  ...selectedAsset,
                                  Address: place?.formatted_address || "",
                                  HasLocation: true,
                                });

                                if (assetResponse.data?.Uuid) {
                                  setSelectedAsset(assetResponse.data);

                                  const res: any = await saveAssetLocation({
                                    assetUuid: assetResponse.data.Uuid || "",
                                    body: [
                                      {
                                        Point: {
                                          type: "Point",
                                          coordinates: [
                                            place?.geometry?.location?.lng(),
                                            place?.geometry?.location?.lat(),
                                          ],
                                        },
                                        Description:
                                          place?.formatted_address || "",
                                        IsCurrent: false,
                                        IsShape: false,
                                      },
                                    ],
                                  });
                                  if (res.error) {
                                    console.log(res.error);
                                    return;
                                  }
                                  toast.success(
                                    "Asset location saved successfully"
                                  );
                                }
                              }
                            }}
                            className="form-control p-2"
                          />
                        </APIProvider>
                        <Input
                          className="mt-3"
                          label="Enter address manually"
                          inputType="textArea"
                          value={[selectedAsset?.Address]}
                          onChange={([v]: any) =>
                            setSelectedAsset({ ...selectedAsset, Address: v })
                          }
                        />
                      </div>
                      <div className="col-lg-4 mb-3 justify-content-between d-flex">
                        <Input
                          label="Has location?"
                          value={[!!selectedAsset?.HasLocation]}
                          onChange={([v]: any) =>
                            setSelectedAsset({
                              ...selectedAsset,
                              HasLocation: v,
                            })
                          }
                          inputType="radio"
                          radioName="hasLocation"
                          options={[
                            { label: "No", value: false },
                            { label: "Yes", value: true },
                          ]}
                        />{" "}
                        {/* <Input
                        label="Can move?"
                        value={[!!selectedAsset?.CanMove]}
                        onChange={([v]: any) =>
                          setSelectedAsset({ ...selectedAsset, CanMove: v })
                        }
                        inputType="radio"
                        radioName="canMove"
                        options={[
                          { label: "No", value: false },
                          { label: "Yes", value: true },
                        ]}
                      /> */}
                      </div>
                    </div>
                  </div>
                  {!selectedAsset?.Uuid && (
                    <div className="alert alert-secondary">
                      Please save the asset to see more options.
                    </div>
                  )}
                  <div className="d-flex justify-content-between align-items-center gap-2">
                    {selectedAsset?.Uuid && (
                      <Button
                        variant="outline-danger"
                        className="mt-3"
                        onClick={() => setShowDeleteAssetModal(true)}
                      >
                        Delete asset
                      </Button>
                    )}
                    <Button
                      variant="colored"
                      className="mt-3"
                      disabled={result.isLoading}
                      onClick={() => {
                        handleSave();
                      }}
                    >
                      Save asset
                    </Button>
                  </div>
                </div>
              </Tab>
              {selectedAsset.Uuid && selectedAsset.HasLocation && (
                <Tab
                  title={
                    <>
                      <span>Asset locations on map </span>
                      <HelpButton helpKey="asset-locations-on-map" />
                    </>
                  }
                  eventKey="locations"
                >
                  <div className="p-3" style={{ height: 700 }}>
                    <AssetLocations
                      asset={selectedAsset}
                      setLocationOverlays={setLocationOverlays}
                      onSave={() => {
                        handleSave();
                      }}
                    />
                  </div>
                </Tab>
              )}
              {selectedAsset.Uuid && selectedAsset.HasLocation && (
                <Tab
                  title={
                    <>
                      <span>Asset locations list </span>
                      <HelpButton helpKey="asset-locations-list" />
                    </>
                  }
                  eventKey="locationsList"
                >
                  <div className="p-3" style={{ minHeight: 700 }}>
                    <AssetLocationsList selectedAsset={selectedAsset} />
                  </div>
                </Tab>
              )}
              {selectedAsset.Uuid && (
                <Tab
                  title={
                    <>
                      <span>Asset contacts </span>
                      <HelpButton helpKey="asset-contacts" />
                    </>
                  }
                  eventKey="contacts"
                >
                  <div className="" style={{ minHeight: 700 }}>
                    <AssetContacts selectedAsset={selectedAsset} />
                  </div>
                </Tab>
              )}
              {selectedAsset.Uuid && (
                <Tab
                  title={
                    <>
                      <span>Asset relations </span>
                      <HelpButton helpKey="asset-relations" />
                    </>
                  }
                  eventKey="relations"
                >
                  <div className="" style={{ minHeight: 700 }}>
                    <AssetRelations selectedAsset={selectedAsset} />
                  </div>
                </Tab>
              )}
                {selectedAsset.Uuid && (
                  <Tab title={<span>Asset logs</span>} eventKey="logs">
                    <div className="p-3" style={{ minHeight: 700 }}>
                      <Logs kind="asset" level="" resource={selectedAsset.Uuid} />
                    </div>
                  </Tab>
                )}
            </Tabs>
          </StyledTabContainer>
          <JsonCollapse json={selectedAsset} />
      </Modal.Body>
      <DeleteAssetsModal
        show={showDeleteAssetModal}
        assetUuids={selectedAsset.Uuid ? [selectedAsset.Uuid] : []}
        assets={selectedAsset.Uuid ? [selectedAsset as AssetType] : []}
        onHide={() => setShowDeleteAssetModal(false)}
        onDeleted={handleClose}
      />
    </>
  );
}

export default function EditAssetModal({
  assetUuid,
  onClose,
  tab = "details",
  modalButton,
  forceShow = false,
}: {
  assetUuid: string;
  onClose?: () => void;
  tab?: string;
  modalButton?: React.ReactNode;
  forceShow?: boolean;
}) {
  const [show, setShow] = useState(false);

  const handleClose = () => {
    setShow(false);
    onClose?.();
  };

  return (
    <>
      {!forceShow && (
        <button
          className="btn p-0 bg-transparent m-0"
          aria-label="Edit asset"
          onClick={() => setShow(true)}
        >
          {modalButton}
        </button>
      )}
      <Modal show={show || forceShow} onHide={handleClose} size="xl">
        <EditAssetForm assetUuid={assetUuid} onClose={handleClose} tab={tab} />
      </Modal>
    </>
  );
}
